import {useContext} from "react";
import {IconAction} from "../../../Form/IconAction/IconAction";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import {I18nReactContext} from "../../../../contexts/I18nContext";
import useSignDocTypes from "../../../../Hooks/useSignDocTypes";
import './CreateWithAIButton.scss';

/**
 * CreateWithAIButton Component
 *
 * A button component that triggers the AI Course Generator Wizard modal.
 * Displays an AutoAwesome icon with tooltip text to indicate its purpose.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Function} props.onClick - Callback function triggered when the button is clicked
 * @param {boolean} [props.disabled=false] - Whether the button is disabled
 * @param {boolean} [props.isAILoading=false] - Indicates if AI generation is in progress (disables button)
 * @param {string} [props.signDocType] - The SignDoc type to get type-specific button text
 * @param {string} [props.buttonText] - Optional custom button text (overrides type-specific text)
 * @returns {JSX.Element} The rendered button component
 */
export const CreateWithAIButton = ({onClick, disabled = false, isAILoading = false, signDocType, buttonText}) => {
    const {getAIGenerationButtonText} = useSignDocTypes();

    // Button is disabled when explicitly disabled or when AI is loading
    const isDisabled = disabled || isAILoading;

    // Get button text: custom prop > type-specific > fallback
    const displayText = buttonText ||
        (getAIGenerationButtonText({ type: signDocType }));

    return (
        <div className="CreateWithAIButton">
            <IconAction
                className="CreateWithAIButton__icon-action"
                onClick={onClick}
                tooltipPlacement="bottom"
                disabled={isDisabled}
                IconComponent={<AutoAwesomeIcon />}
                text={displayText}
                dataTestId="create-with-ai-button"
            />
        </div>
    );
};
